import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { UnitType } from 'entities/Contract';
import { UserAccountType } from 'entities/User';
import { WarehouseForBooking } from 'entities/Warehouse';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';
import { Box, CachedBox } from '../../model/types';
import { getBoxDiscountedPriceAmount } from '../helpers/getBoxDiscountedPriceAmount';
import { getBoxPriceAmountWithVat } from '../helpers/getBoxPriceAmountWithVat';
import { parseDiscountData } from '../helpers/parseDiscountData';

export const useGetBoxDiscountedPriceString = ({
  warehouseInfo,
  boxInfo,
  showPriceWithVat,
  accountType,
  unitType = UnitType.BOX,
}: {
  warehouseInfo: Nullable<WarehouseForBooking> | undefined;
  boxInfo: Nullable<Box> | Nullable<CachedBox>;
  showPriceWithVat?: boolean;
  accountType?: Nullable<UserAccountType>;
  unitType?: Nullable<UnitType>;
}): string | undefined => {
  const { t } = useAppTranslation('booking');

  const currencySymbol = useGetCurrencySymbol();

  const warehouseDiscounts = warehouseInfo?.warehouseDiscounts;

  const firstMonthDiscounts = warehouseDiscounts
    ?.filter(
      (discount) => discount.unitType === unitType && discount.discounts.split(',').some((d) => FIRST_MONTH_DISCOUNT_REGEX.test(d.trim())),
    )
    .sort((a, b) => a.priority - b.priority);

  let actualDiscount = firstMonthDiscounts?.find((discount) => !discount.unitSizes) || firstMonthDiscounts?.[0];

  if (boxInfo?.sizeCode.square) {
    actualDiscount = firstMonthDiscounts?.find((discount) => {
      const unitSizes = discount.unitSizes?.split(',').map((size) => Number(size));

      return unitSizes?.includes(boxInfo.sizeCode.square);
    });
  }

  const discountData = parseDiscountData(actualDiscount);

  if (!actualDiscount || !discountData) {
    return undefined;
  }

  const getRatePrice = (): number | undefined => {
    if (boxInfo?.monthRate) {
      return roundNumber(boxInfo.monthRate * boxInfo.priceFactor);
    }
    if (boxInfo?.weekRate) {
      return roundNumber(boxInfo.weekRate * boxInfo.priceFactor);
    }
    if (boxInfo?.dailyRate) {
      return roundNumber(boxInfo.dailyRate * boxInfo.priceFactor);
    }
  };

  const discountedBoxPriceAmount = getBoxDiscountedPriceAmount({
    warehouseDiscounts,
    boxPrice: getRatePrice(),
    boxSquare: boxInfo?.sizeCode.square,
  });

  if (!discountedBoxPriceAmount) {
    return undefined;
  }

  const boxPriceAmountWithVat = getBoxPriceAmountWithVat(discountedBoxPriceAmount, warehouseInfo, accountType);

  const discountedPrice = showPriceWithVat ? boxPriceAmountWithVat : discountedBoxPriceAmount;

  if (boxInfo?.monthRate) {
    return discountData.allDurationEqual
      ? `${roundNumber(discountedPrice)} ${currencySymbol} ${t('for {{discountDuration}} month(s)', {
          discountDuration: t(discountData.duration),
        })}`
      : `${roundNumber(discountedPrice)} ${currencySymbol} ${t('for {{discountDuration}} month(s)', {
          discountDuration: 1,
        })}`;
  }

  if (boxInfo?.weekRate) {
    return discountData.allDurationEqual
      ? `${roundNumber(discountedPrice)} ${currencySymbol} ${t('for {{discountDuration}} week(s)', {
          discountDuration: t(discountData.duration),
        })}`
      : `${roundNumber(discountedPrice)} ${currencySymbol}`;
  }

  if (boxInfo?.dailyRate) {
    return discountData.allDurationEqual
      ? `${roundNumber(discountedPrice)} ${currencySymbol} ${t('for {{discountDuration}} day(s)', {
          discountDuration: t(discountData.duration),
        })}`
      : `${roundNumber(discountedPrice)} ${currencySymbol}`;
  }
};
